import React, { Component } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Slide,
    TextField,
    FormGroup,
    IconButton,
    Popover
} from "@mui/material";
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { TransitionProps } from "@mui/material/transitions";
import InputTextField from "../Reusables/InputTextField";
import { amountFormat } from "../helper";
import PromoCodeServices from "../../Services/PromoCodeService";
import { ShowAlert } from "../ShowAlert";
import PromotionalCode from "../Reusables/CodeGenerator";
import { DateRange } from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import BigButton from "../BigButton";
import moment from "moment";
import { Messages } from "../Reusables/Utility";

// Interface for AddCashBackCouponProps
interface AddCashBackCouponProps {
    open: boolean;
    accessToken: string;
    onClose?: (state: boolean, action: string) => void;
    refreshData: () => void;
}

//  Interface for mainclass state
interface AddCashBackCouponState {
    cashBackCoupon: string;
    codeValue: string;
    codeValueError: string,
    minTransactionAmt: string,
    minTransactionAmtError: string,
    accessToken: string;
    selectedDateRange: { startDate: Date; endDate: Date; };
    popOverTargetElementForDateRange: HTMLElement | null,
    popOverTargetElementForTempDateRange: HTMLElement | null,
    iscodeValid: boolean;
}

// Transition component
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Main class for AddCashBackCoupon component
class AddCashBackCoupon extends Component<AddCashBackCouponProps, AddCashBackCouponState> {
    constructor(props: AddCashBackCouponProps) {
        super(props);
        this.state = {
            cashBackCoupon: "",
            codeValue: "0.00",
            codeValueError: "",
            minTransactionAmt: "",
            minTransactionAmtError: "",
            accessToken: "",
            selectedDateRange:
            {
                startDate: new Date(),
                endDate: new Date()
            },
            popOverTargetElementForDateRange: null,
            popOverTargetElementForTempDateRange: null,
            iscodeValid: true
        };
    }

    // Get access token and influence list for the component
    async componentDidMount() {
        const accesstoken_ = await loadFromLocalStorage('accessToken');

        if (accesstoken_) {
            this.setState({
                accessToken: accesstoken_.toString()
            })
        }
        else {
            localStorage.clear();
            window.location.href = '/'
        }
    }

    //Update generated code
    onGenerate = (newCode: string) => {
        this.setState({ cashBackCoupon: newCode, iscodeValid: false });
    };

    // Handle promo code change & validation
    handleOnChange = (newCode: string, isCodeValid: boolean) => {
        this.setState({
            cashBackCoupon: newCode,
            iscodeValid: isCodeValid, // Only valid if greter than 4 characters
        });
    };

    // HandleOpenPopover function to handle the calender 
    private handleOpenPopover = (
        event: React.MouseEvent<HTMLElement>,
        popoverType: 'dateRange' | 'tempDateRange'
    ) => {
        if (popoverType === 'dateRange') {
            this.setState({ popOverTargetElementForDateRange: event.currentTarget });
        } else {
            this.setState({ popOverTargetElementForTempDateRange: event.currentTarget });
        }
    };

    // Handle apply date range from datepicker
    private applyDateRange = (updateValue: keyof AddCashBackCouponState, popoverType: 'dateRange' | 'tempDateRange') => {
        console.log(updateValue, popoverType)

        this.setState((prevState) => ({
            ...prevState,
            [updateValue]: this.state[updateValue],
            [popoverType === 'dateRange'
                ? 'popOverTargetElementForDateRange'
                : 'popOverTargetElementForTempDateRange']: null,
        }));
    };

    // HandleSelectDateRange function to handle the date range
    private handleSelectDateRange = (ranges: any, updateValue: keyof AddCashBackCouponState) => {
        const selectedRange = ranges.range1;
        this.setState((prevState) => ({
            ...prevState,
            [updateValue]: selectedRange,
        }));
    };

    // Method on save button click api call...
    private handleDialogAction = async (action: string) => {
        if (action === "ok") {
            const { cashBackCoupon, codeValue, minTransactionAmt, selectedDateRange } = this.state;
            let codeValueError = "";
            let minTransactionAmtError = "";

            const sanitizedCodeValue = codeValue ? parseFloat(codeValue.replace(/,/g, "")) : 0;
            const sanitizedMinTransactionAmt = minTransactionAmt ? parseFloat(minTransactionAmt.replace(/,/g, "")) : 0;

            // Validate codeValue
            if (sanitizedCodeValue <= 0) {
                codeValueError = Messages.Errors.couponAmount
            }

            // Validate minTransactionAmt
            if (sanitizedMinTransactionAmt < sanitizedCodeValue) {
                minTransactionAmtError = Messages.Errors.minTransactionAmt
            }

            // If validation fails, update state and stop execution
            if (codeValueError || minTransactionAmtError) {
                this.setState({ codeValueError, minTransactionAmtError });
                return;
            }

            const startDate = moment(selectedDateRange.startDate).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
            const endDate = moment(selectedDateRange.endDate).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');

            // Prepare postData
            const postData: any = {
                couponCode: cashBackCoupon,
                amount: sanitizedCodeValue,
                minTransactionAmt: sanitizedMinTransactionAmt,
                startTime: startDate,
                endTime: endDate
            };

            try {
                const rsp = await PromoCodeServices.storeCashBackCoupon(this.props.accessToken, postData);
                if (rsp?.ok) {
                    await this.props.refreshData();

                    this.props.onClose?.(true, action);
                    ShowAlert("Success", Messages.Success.cashBackCodesaved, "success");
                } else {
                    ShowAlert("Alert", Messages.Errors.apiError, "error");
                }
            } catch (error) {
                console.error(Messages.Errors.unexpected, error);
                ShowAlert("Error", Messages.Errors.unexpected, "error");
            }
        } else {
            this.props.onClose?.(false, action);
        }
    };

    render() {
        const { open, onClose } = this.props;
        const { cashBackCoupon, iscodeValid, codeValue, codeValueError, minTransactionAmt, minTransactionAmtError, selectedDateRange, popOverTargetElementForDateRange } = this.state;
        const isSubmitDisabled = !(cashBackCoupon && !iscodeValid && codeValue && minTransactionAmt); // Disable submit button if any of the fields are empty
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionComponent={Transition} keepMounted>
                <DialogTitle>Add Cashback Coupon</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" marginTop="12px">
                        <PromotionalCode
                            value={cashBackCoupon}
                            label="Enter code or generate"
                            placeholder="Enter code or generate"
                            onGenerate={this.onGenerate}
                            onChange={this.handleOnChange}
                        />
                        <Box display="flex" flexDirection="column" gap={3} marginTop="12px">
                            <InputTextField
                                value={codeValue}
                                startAdornment="$"
                                inputType="number"
                                onChange={(value) => this.handleLimitChange("codeValue", value)}
                                onBlur={() => this.handleLimitBlur("codeValue")}
                                onFocus={() => this.handleLimitFocus('codeValue')}
                                error={!!codeValueError}
                                helperText={codeValueError}
                                placeholder="Enter Amount"
                            />
                            <InputTextField
                                value={minTransactionAmt}
                                startAdornment="$"
                                inputType="number"
                                onChange={(value) => this.handleLimitChange("minTransactionAmt", value)}
                                onBlur={() => this.handleLimitBlur("minTransactionAmt")}
                                onFocus={() => this.handleLimitFocus('minTransactionAmt')}
                                error={!!minTransactionAmtError}
                                helperText={minTransactionAmtError}
                                placeholder="Min transaction"
                                filedLable="Min transaction"
                            />
                            <FormGroup>
                                <TextField
                                    variant="outlined"
                                    label="Expiration"
                                    color="secondary"
                                    sx={{ minWidth: "250px", marginTop: "20px" }}
                                    InputProps={{
                                        readOnly: true, // Prevents manual input if only selection is allowed
                                        endAdornment: (
                                            <IconButton onClick={(event) => this.handleOpenPopover(event, 'dateRange')}>
                                                <DateRange />
                                            </IconButton>
                                        ),
                                    }}
                                    value={`Valid From: ${selectedDateRange.startDate.toLocaleDateString()} - Valid To: ${selectedDateRange.endDate.toLocaleDateString()}`}
                                />
                                <Popover
                                    id={'date-range-popover'}
                                    open={Boolean(popOverTargetElementForDateRange)}
                                    anchorEl={popOverTargetElementForDateRange}
                                    onClose={() =>
                                        this.setState({ popOverTargetElementForDateRange: null })
                                    }
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    <Box className="popover-calendar">
                                        <DateRangePicker
                                            ranges={[selectedDateRange]}
                                            onChange={(ranges: any) => {
                                                this.handleSelectDateRange(ranges, 'selectedDateRange');
                                            }}
                                            minDate={new Date()}
                                        />
                                        <BigButton
                                            disabled={false}
                                            width="80%"
                                            onClick={() => {
                                                this.applyDateRange('selectedDateRange', 'dateRange');
                                            }}
                                            buttonText="Apply"
                                        />
                                    </Box>
                                </Popover>
                            </FormGroup>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.handleDialogAction("cancel")}>Cancel</Button>
                    <Button color="secondary" variant="contained"
                        onClick={() => this.handleDialogAction("ok")}
                        disabled={isSubmitDisabled}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    handleLimitChange = (field: keyof AddCashBackCouponState, value: string) => {
        console.log(field);
        let sanitizedValue = value.replace(/[^0-9.]/g, "");
        const MAX_LIMIT = 999999999;

        // Prevent multiple decimal points
        if ((sanitizedValue.match(/\./g) || []).length > 1) {
            sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf(".") + 1);
        }

        // Ensure value does not exceed max limit
        if (parseFloat(sanitizedValue) > MAX_LIMIT) {
            sanitizedValue = MAX_LIMIT.toString();
        }

        let codeValueError = "";
        let minTransactionAmtError = "";

        switch (field) {
            case "codeValue":
                if (sanitizedValue === "" || sanitizedValue === "0.00" || parseFloat(sanitizedValue) === 0) {
                    codeValueError = Messages.Errors.couponAmount
                }
                break;

            case "minTransactionAmt":
                if (
                    sanitizedValue === "" ||
                    sanitizedValue === "0.00" ||
                    parseFloat(sanitizedValue) === 0 ||
                    (this.state.codeValue && parseFloat(sanitizedValue) < parseFloat(this.state.codeValue))
                ) {
                    minTransactionAmtError = Messages.Errors.minTransactionAmt
                }
                break;

            default:
                break;
        }

        this.setState((prevState) => ({
            ...prevState,
            [field]: sanitizedValue,
            codeValueError,
            minTransactionAmtError
        }));
    };


    // Handle code value blur
    handleLimitBlur = (field: keyof AddCashBackCouponState) => {
        let value = this.state[field];

        if (typeof value !== "string") {
            value = "0";
        }

        if (!value || isNaN(parseFloat(value))) {
            value = "0";
        }
        const sanitizedValue = value.replace(/,/g, "");
        const updatedAmount = amountFormat(parseFloat(sanitizedValue), "en-US", "USD").replace("$", "");

        this.setState((prevState) => ({
            ...prevState,
            [field]: updatedAmount,
        }));
    };

    // Handle code value focus
    handleLimitFocus = (field: keyof AddCashBackCouponState) => {
        const value = this.state[field];
        if (typeof value === 'string') {
            const n_ = value.replace(/,/g, "");
            this.setState((prevState) => ({
                ...prevState,
                [field]: n_,
                [`${field}InputType`]: "number"
            }));
        }
    };
}

export default AddCashBackCoupon;
